import type { GeometryElement } from 'jsxgraph';
import type { ElementCreatorContext } from '../core/board.types';
import type { SnappingConfig } from './snapping.model';

/**
 * Build JSXGraph attributes from a snapping configuration.
 *
 * @param ctx - Element creator context containing the board
 * @param config - Snapping configuration
 * @returns Record of JSXGraph attributes for snapping behavior
 */
export function buildSnappingAttributes(
  ctx: ElementCreatorContext,
  config: SnappingConfig | undefined,
): Record<string, unknown> {
  if (!config) {
    return {};
  }

  const attrs: Record<string, unknown> = {};

  // Grid snapping
  if (config.grid) {
    attrs['snapToGrid'] = config.grid.enabled;
    if (config.grid.enabled) {
      if (config.grid.sizeX !== undefined) {
        attrs['snapSizeX'] = config.grid.sizeX;
      }
      if (config.grid.sizeY !== undefined) {
        attrs['snapSizeY'] = config.grid.sizeY;
      }
    }
  }

  // Attractor snapping
  if (config.attractors?.attractors?.length) {
    attrs['attractors'] = resolveAttractors(ctx, config.attractors.attractors);

    if (config.attractors.distance !== undefined) {
      attrs['attractorDistance'] = config.attractors.distance;
    }
    if (config.attractors.snatchDistance !== undefined) {
      attrs['snatchDistance'] = config.attractors.snatchDistance;
    }
    if (config.attractors.unit !== undefined) {
      attrs['attractorUnit'] = config.attractors.unit;
    }
  }

  return attrs;
}

/**
 * Resolve attractor references to actual JSXGraph elements.
 * Accepts both string IDs and direct element references.
 *
 * @param ctx - Element creator context containing the board
 * @param attractors - Array of element IDs or element references
 * @returns Array of resolved JSXGraph elements
 * @throws Error if a string ID cannot be resolved to an element
 */
function resolveAttractors(ctx: ElementCreatorContext, attractors: Array<string | GeometryElement>): GeometryElement[] {
  return attractors.map((ref) => {
    if (typeof ref === 'string') {
      const element = ctx.board.elementsByName[ref];
      if (!element) {
        throw new Error(`Attractor element "${ref}" not found on board`);
      }
      return element as GeometryElement;
    }
    return ref;
  });
}
